import { useEffect } from 'react'
import { useElementScrollRestoration, useRouterState } from '@tanstack/react-router'

/**
 * Voltar devolve a página onde ela estava.
 *
 * ## O router rolava para o topo em TODA navegação, inclusive no voltar
 *
 * `scrollRestoration` nasce `false` no TanStack, e com ela desligada o router
 * ainda assina `onRendered` e chama `scrollTo({ top: 0 })` a cada rota — o que
 * está certo para quem entra numa tela nova e errado para quem volta. Ligar a
 * opção resolve metade: ela passa a guardar a posição por entrada do histórico
 * (e a pôr `history.scrollRestoration = 'manual'`, tirando o navegador da
 * disputa) e a reaplicar no voltar.
 *
 * ## A outra metade: a página ainda não tem a altura que tinha
 *
 * `useMonthlyBook` lê o mês do Firestore num `useEffect` — não há cache. No
 * instante em que o router restaura, a lista de setembro ainda não existe: o
 * documento tem a altura do cabeçalho, e `scrollTo({ top: 7400 })` é **cortado
 * pelo máximo rolável**, sem erro nenhum. Meio segundo depois as transações
 * chegam, a página cresce — e a rolagem continua onde o corte deixou, perto do
 * topo. É exatamente o sintoma relatado.
 *
 * Então esta camada espera: guarda o alvo que o router queria, observa a página
 * crescer e reaplica assim que a posição couber.
 *
 * ## Ela desiste por tempo, e desiste na hora se a pessoa mexer
 *
 * Duas saídas, e as duas importam. O prazo existe porque uma tela pode
 * simplesmente nunca voltar a ter aquela altura — a leitura falhou, o mês mudou
 * de tamanho, o filtro era outro —, e um observador imortal ficaria armado para
 * dar um pulo muito depois de a pessoa ter começado a ler.
 *
 * O gesto existe porque restaurar é um palpite sobre o que a pessoa quer, e no
 * momento em que ela rola, toca ou usa o teclado o palpite acabou: puxar a
 * página para outro lugar por baixo do dedo é pior do que ter perdido a posição.
 */

/** O quanto esperar o conteúdo chegar antes de largar o alvo. */
const ESPERA_MS = 3000

/**
 * Os gestos que cancelam a restauração.
 *
 * `scroll` **não** serve: o próprio `scrollTo` do router dispara um, e a
 * restauração se cancelaria sozinha. Estes três são intenção de quem está na
 * frente da tela, e nenhum deles é efeito de algo que este código fez.
 */
const INTERROMPEM = ['wheel', 'touchstart', 'keydown'] as const

/** A posição cabe no documento com a altura que ele tem agora? */
export function alcancavel(alvo: number, alturaDoDocumento: number, alturaDaJanela: number) {
  return alturaDoDocumento - alturaDaJanela >= alvo
}

export function useRestaurarRolagem() {
  // A entrada que o próprio router guardou para ESTA posição do histórico.
  // Existe só quando já estivemos aqui e rolamos — numa navegação nova a chave
  // é inédita, e o hook devolve `undefined`. É o que faz esta camada agir no
  // voltar e ficar quieta no avançar, sem precisar perguntar qual foi.
  const entrada = useElementScrollRestoration({ getElement: () => window })
  const href = useRouterState({ select: (estado) => estado.location.href })
  const alvo = entrada?.scrollY ?? 0

  useEffect(() => {
    if (alvo <= 0) return
    // O router já conseguiu aplicar: a página tinha a altura toda (veio do
    // cache do Firestore, ou a tela não depende de leitura). Nada a fazer.
    if (Math.abs(window.scrollY - alvo) <= 1) return
    // O jsdom não tem `ResizeObserver`, e sem layout não haveria o que observar.
    if (typeof ResizeObserver === 'undefined') return

    let observador: ResizeObserver | null = null

    const parar = () => {
      observador?.disconnect()
      observador = null
      clearTimeout(prazo)
      for (const gesto of INTERROMPEM) window.removeEventListener(gesto, parar)
    }

    const tentar = () => {
      if (observador === null) return
      if (!alcancavel(alvo, document.documentElement.scrollHeight, window.innerHeight)) return
      window.scrollTo(0, alvo)
      parar()
    }

    const prazo = setTimeout(parar, ESPERA_MS)
    observador = new ResizeObserver(tentar)
    observador.observe(document.body)
    for (const gesto of INTERROMPEM) window.addEventListener(gesto, parar, { passive: true })
    // Uma tentativa imediata: entre o `onRendered` do router e este efeito a
    // página pode já ter crescido, e aí não há o que esperar.
    tentar()

    return parar
    // `href` entra junto porque duas telas diferentes podem ter sido deixadas
    // na mesma altura: sem ele, voltar para a segunda não reexecutaria nada.
  }, [alvo, href])
}
